import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactGa from 'react-ga'
import { Badge, Button, Grid, Hidden, Typography, makeStyles } from '@material-ui/core'
import clsx from 'clsx'

import WorkIcon from '@material-ui/icons/Work'
import AccountBalanceIcon from '@material-ui/icons/AccountBalance'
import AssignmentIcon from '@material-ui/icons/Assignment'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'

import Filter from './Filter'
import * as actions from '../actionTypes'
import { getSelectedFiltersCount } from '../utils'

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    backgroundColor: `#FFFFFF`,
    width: '100%',
    padding: '0px 36px 40px',
    marginBottom: '79px',
    boxShadow: '0 2px 2px #70707066',
    [theme.breakpoints.down('sm')]: {
      padding: '0px 0px 36px',
    },
  },
  mainContainerClicked: {
    [theme.breakpoints.down('md')]: {
      marginBottom: 0,
      boxShadow: 'none',
    },
  },
  subContainer: {
    maxWidth: '1640px',
    margin: 'auto',
    padding: '2rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0px 36px',
      maxWidth: '100%',
    },
  },
  subContainerAbsolute: {
    width: '100%',
    position: 'absolute',
    backgroundColor: 'white',
    left: 0,
    bottom: 0,
    height: 'calc(100vh - 56px)',
  },
  heading: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 'bolder',
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },
  },
  filterButtonsContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  filterButtonsContainerClicked: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  mobileOnly: {
    margin: '30px 0',
    width: '100%',
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
  },
  desktopOnly: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  horizontal: {
    width: '100%',
    border: '0.1px solid #eee',
    marginTop: 40,
    marginBottom: 40,
    height: 0,
  },
  buttons: {
    height: 56,
    textAlign: 'center',
    display: 'grid',
    alignItems: 'center',
    width: '23%',
    [theme.breakpoints.down('md')]: {
      height: 40,
      width: '100%',
      marginBottom: 40,
    },
  },
  buttonSearch: {
    width: 198,
    height: 48,
    fontSize: theme.typography.pxToRem(18),
  },
  buttonApply: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignItems: 'center',
      height: 40,
      fontSize: theme.typography.pxToRem(18),
      justifyContent: 'space-around',
    },
  },
  containerButtons: {
    justifyContent: 'flex-end',
    fontSize: theme.typography.pxToRem(18),
    alignItems: 'center',
    margin: 'auto',
    [theme.breakpoints.down('md')]: {
      maxWidth: 'none',
      marginBottom: theme.spacing(1),
      justifyContent: 'space-evenly',
    },
  },
  mobileButtons: {
    justifyContent: 'space-evenly',
    fontSize: theme.typography.pxToRem(18),
    alignItems: 'center',
    margin: 'auto',
    width: '100%',
    flex: 1,
    [theme.breakpoints.down('md')]: {
      maxWidth: 'none',
      margin: '25px 0',
    },
  },
  resetButton: {
    fontSize: '18px',
    cursor: 'pointer',
    color: '#1E2553',
    fontWeight: '700',
    textTransform: 'uppercase',
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(4),
    },
  },
  hidden: {
    display: 'none',
  },
}))

const FilterForm = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { filters, viewFiltersStatus } = useSelector((state) => state.projectBoard)

  const positionArray = filters.positionType.available
  const attendingArray = filters.attending.available
  const departmentArray = filters.department.available
  const divisionArray = filters.division.available

  const selectedFiltersCount = getSelectedFiltersCount(filters)

  const filterDescriptors = [
    {
      text: 'Filter by Position',
      filterArray: positionArray,
      backgroundColor: '#e1eef6',
      filterName: 'positionType',
      icon: <WorkIcon color="primary" />,
      ref: useRef(),
    },
    {
      text: 'Filter by Department',
      filterArray: departmentArray,
      backgroundColor: '#ecd3e5',
      filterName: 'department',
      icon: <AccountBalanceIcon color="primary" />,
      ref: useRef(),
    },
    {
      text: 'Filter by Division',
      filterArray: divisionArray,
      backgroundColor: '#FDF2DF',
      filterName: 'division',
      icon: <AssignmentIcon color="primary" />,
      ref: useRef(),
    },
    {
      text: 'Filter by Attending',
      filterArray: attendingArray,
      backgroundColor: '#DBF0E0',
      filterName: 'attending',
      icon: <AccountCircleIcon color="primary" />,
      ref: useRef(),
    },
  ]

  useEffect(() => {
    dispatch({ type: actions.FILTER_REQUEST })
  }, [dispatch])

  // bind handler to close filter menus
  useEffect(() => {
    const hideOpenMenus = (event) => {
      filterDescriptors.forEach(({ ref }) => {
        if (!ref?.current?.node?.contains(event.target)) {
          ref.current.close() // See useImperativeHandle in Filter.js
        }
      })
    }
    document.addEventListener('click', hideOpenMenus)
    return () => document.removeEventListener('click', hideOpenMenus)
  }, [filterDescriptors])

  const resetFilters = () => {
    dispatch({
      type: actions.FILTER_SELECTION_REQUEST_ALL_DESELECT,
    })
  }

  const filterSelectionRequest = () => {
    ReactGa.event({
      category: "Search Project",
      action: "Click To Search"
    })
    dispatch({
      type: actions.PROJECT_BOARD_ACTIVE_PROJECTS_REQUEST,
      payload: { pageNumber: 1, scrollToTop: true },
    })
    dispatch({ type: actions.HIDE_FILTERS_MOBILE })
  }

  const viewFiltersMobile = () => {
    dispatch({ type: actions.VIEW_FILTERS_MOBILE })
  }

  return (
    <Grid
      container
      className={clsx(classes.mainContainer, viewFiltersStatus && classes.mainContainerClicked)}
    >
      <Grid container className={classes.subContainer}>
        <Grid item className={clsx(viewFiltersStatus && classes.desktopOnly)}>
          <Typography className={classes.heading} color="primary" gutterBottom variant="h1">
            Welcome! Find all open research projects here.
          </Typography>
          <Typography gutterBottom variant="body1">
            Join the community and best resource to discover and connect with doctors and research
            projects all across campus.
          </Typography>
        </Grid>
        <Grid
          container
          justify="space-between"
          className={clsx(
            classes.filterButtonsContainer,
            viewFiltersStatus && classes.filterButtonsContainerClicked
          )}
        >
          <Typography
            className={clsx(classes.heading, classes.mobileOnly)}
            color="primary"
            variant="h1"
          >
            Project Filters
          </Typography>

          {filterDescriptors.map((filterDescriptor) => (
            <Grid item className={classes.buttons} key={filterDescriptor.text}>
              <Filter {...filterDescriptor} />
            </Grid>
          ))}
        </Grid>
        <div className={classes.horizontal}></div>
        <Grid container className={classes.mobileButtons}>
          {!viewFiltersStatus && (
            <Button
              color="primary"
              fullWidth
              variant="contained"
              className={classes.buttonApply}
              onClick={() => viewFiltersMobile()}
            >
              <Badge badgeContent={selectedFiltersCount} color="secondary">
                <svg
                  style={{ marginLeft: '1em' }}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  height="1em"
                  width="1em"
                >
                  <path
                    className="cls-1"
                    d="M43,26.41v2.23h6.67V26.41Zm0-13.33V15.3H54.11V13.08ZM54.11,30.86V28.64H63V26.41H54.11V24.19H51.89v6.67ZM47.44,17.52v2.23H43V22h4.44v2.22h2.23V17.52ZM63,22V19.75H51.89V22Zm-6.67-4.45h2.23V15.3H63V13.08H58.56V10.86H56.33Z"
                    transform="translate(-43 -10.86)"
                  />
                </svg>
              </Badge>
              <span style={{ flexGrow: 2, marginRight: '2em' }}>View Filters</span>
            </Button>
          )}
          {viewFiltersStatus && (
            <Button
              color="primary"
              fullWidth
              variant="contained"
              className={classes.buttonApply}
              onClick={() => filterSelectionRequest()}
            >
              Apply Filters
            </Button>
          )}
        </Grid>
        <Grid container className={classes.containerButtons}>
          <Button
            disabled={selectedFiltersCount === 0}
            className={classes.resetButton}
            onClick={resetFilters}
          >
            Reset
          </Button>
          <Hidden mdDown>
            <Grid item>
              <Button
                color="primary"
                fullWidth
                variant="contained"
                className={classes.buttonSearch}
                onClick={() => filterSelectionRequest()}
              >
                Search
              </Button>
            </Grid>
          </Hidden>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default FilterForm
